<template>
  <div class="w-full h-full">
    <div class="flex justify-between w-full px-2">
      <div class="flex items-center mt-2 space-x-4 text-2xl font-medium">
        <ChevronLeft
          class="text-gray-600 cursor-pointer h-7 w-7 dark:text-dark-body-text hover:text-common-grey"
          aria-hidden="true"
          @click="$router.push(`/playbooks`)" />
        <div class="flex justify-between w-full">
          <div class="flex items-center justify-center gap-1">
            <div>
              <span class="text-2xl font-medium">{{ playbook.name }}</span>
            </div>
            <Pencil
              class="w-4 h-4 cursor-pointer"
              aria-hidden="true"
              @click="$emitter.emit(ALL_EMITTERS.CREATE_NEW_PLAYBOOK, { playbook })" />
          </div>
        </div>
      </div>

      <div class="flex gap-2 mt-2">
        <button
          v-if="!isNewPlaybook && playbook_updated"
          class="flex items-center justify-center flex-none w-10 h-10 p-1.5 transition-opacity rounded-md border bg-white hover:bg-gray-200"
          @click.stop="cancelEditPlaybook()">
          <X class="w-5 h-5 text-red-500" aria-hidden="true" />
        </button>
        <button v-if="isNewPlaybook || playbook_updated" class="h-10 btn primary-btn w-fit" @click="saveBtnClicked">
          <Rocket v-if="isNewPlaybook" class="-ml-2 w-7 h-7" color="#FFF" fill="#FFF" />
          <ClipboardList v-else class="w-5 h-5 -ml-2" aria-hidden="true" />
          <span class="ml-2">{{ isNewPlaybook ? $t("Launch Playbook") : $t("Save changes") }}</span>
        </button>
        <SelectInput
          v-if="!isNewPlaybook"
          v-model="selected_status"
          :value="selected_status"
          class="w-60"
          name="view"
          :allow-null="false"
          :items="status_options"
          label-attr="label"
          :is-box-background="false"
          value-attr="value"
          placeholder=""
          :label="$t('View')"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />

        <SelectInput
          v-model="selected_view"
          class="ml-1 w-60"
          name="view"
          :allow-null="false"
          :items="view_options"
          label-attr="label"
          :is-box-background="false"
          value-attr="value"
          placeholder=""
          :label="$t('View')"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
      </div>
    </div>

    <transition name="fade" mode="out-in" duration="100">
      <div v-if="new_node_data" class="absolute ml-6 mt-4 z-50">
        <AddNewItemsMenu
          v-click-outside="
            () => {
              !prevent_click_outside_trigger ? (new_node_data = null) : null
            }
          "
          :get-connected-elements="getConnectedElements"
          :get-connected-source-elements="getConnectedSourceElements"
          :elements="elements"
          :data="new_node_data.data"
          @select-item="($event) => handleSelectItem($event, 'b')" />
      </div>
    </transition>
    <VueFlow
      v-if="selected_view == 'sequence'"
      v-model="elements"
      class="mt-16"
      default-marker-color="black"
      :delete-key-code="null"
      @edge-mouse-leave="edge_hover = null"
      @click="source_connection_active = null"
      @node-drag="handleDrag"
      @edge-mouse-enter="handleEdgeMouseOver">
      <template #node-branch="props">
        <StartNode
          v-bind="props"
          :dimensions="node_dimensions.branch[props.data.type]"
          :get-connected-elements="getConnectedElements"
          :get-connected-source-elements="getConnectedSourceElements"
          :source-connection-active="source_connection_active"
          :target-connection-active="target_connection_active"
          :add-new-node="handleAddNewNode" />
      </template>
      <template #node-activity="props">
        <ActivityNode
          :new-node="new_node_data"
          :filtered-company-id="filtered_company_id"
          :selected-node="selected_node"
          :elements="elements"
          :get-connected-elements="getConnectedElements"
          :get-connected-source-elements="getConnectedSourceElements"
          v-bind="props"
          :dimensions="node_dimensions.activity[props.data.type]"
          :source-connection-active="source_connection_active"
          :target-connection-active="target_connection_active"
          :add-new-node="handleAddNewNode" />
      </template>
      <template #node-circle="props">
        <CircleNode
          :new-node="new_node_data"
          :elements="elements"
          :get-connected-elements="getConnectedElements"
          :get-connected-source-elements="getConnectedSourceElements"
          v-bind="props"
          :dimensions="node_dimensions.circle[props.data.type]"
          :source-connection-active="source_connection_active"
          :target-connection-active="target_connection_active"
          :add-new-node="handleAddNewNode" />
      </template>
      <template #node-selection="props">
        <SelectionNode
          :new-node="new_node_data"
          :elements="elements"
          :get-connected-elements="getConnectedElements"
          :get-connected-source-elements="getConnectedSourceElements"
          v-bind="props"
          :dimensions="node_dimensions.selection[props.data.type]"
          :source-connection-active="source_connection_active"
          :target-connection-active="target_connection_active"
          :add-new-node="handleAddNewNode" />
      </template>
      <template #edge-custom="props">
        <CustomEdge
          :delete-node="handleDeleteNode"
          :remove-merge="removeMergeOrRearrangeItsConnections"
          :remove-split="removeMergeOrRearrangeItsConnections"
          :get-connected-source-elements="getConnectedSourceElements"
          :elements="elements"
          :edge-hover="edge_hover"
          v-bind="props"
          :get-connected-elements="getConnectedElements" />
      </template>
      <Controls position="top-left" />
      <Background />
    </VueFlow>
    <PlaybookCodeEditor v-else-if="selected_view == 'editor'" :playbook="playbook" @change="($event) => handleChangeEditor($event)" />
    <PlaybookExecutions v-else-if="selected_view == 'audit'" :playbook="playbook" />
    <PlaybookFormsDrawer
      v-if="drawer_open"
      v-model:is-open="drawer_open"
      :elements="elements"
      :selected-node="selected_node"
      :get-connected-source-elements="getConnectedSourceElements"
      @submit="
        ($event) =>
          handleSubmitNodeForm($event, () => {
            drawer_open = false
          })
      "
      @update-references="handleUpdateReferences" />
  </div>
</template>

<script>
import "@vue-flow/controls/dist/style.css"
import { Background } from "@vue-flow/background"
import { Controls } from "@vue-flow/controls"
import AddNewItemsMenu from "./nodes/AddNewItemsMenu.vue"
import { VueFlow, MarkerType } from "@vue-flow/core"
import PlaybookFormsDrawer from "./forms"
import StartNode from "./nodes/StartNode.vue"
import ActivityNode from "./nodes/ActivityNode.vue"
import CircleNode from "./nodes/CircleNode.vue"
import SelectionNode from "./nodes/SelectionNode.vue"
import CustomEdge from "./CustomEdge.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import PlaybookExecutions from "@/views/dashboard/playbooks/PlaybookExecutions.vue"
import PlaybookCodeEditor from "@/views/dashboard/playbooks/PlaybookCodeEditor.vue"
import { transformServerFilterToMetricFilter } from "@/services/data.transformers"
import { Rocket, X, ChevronLeft, Pencil, ClipboardList } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
export default {
  name: "NewPlaybook",
  components: {
    Background,
    Controls,
    AddNewItemsMenu,
    Pencil,
    Rocket,
    X,
    ClipboardList,
    ChevronLeft,
    VueFlow,
    StartNode,
    ActivityNode,
    CircleNode,
    SelectionNode,
    CustomEdge,
    PlaybookFormsDrawer,
    SelectInput,
    PlaybookExecutions,
    PlaybookCodeEditor
  },
  beforeRouteLeave() {
    if ((this.isNewPlaybook || this.playbook_updated) && this.save_playbook_button_clicked == false) {
      const answer = window.confirm(this.$t("You have unsaved changes, playbook will be reverted if you don't save"))
      if (!answer) {
        return false
      }
    }
  },
  props: {
    isNewPlaybook: {
      type: Boolean,
      default: false
    }
  },
  emits: [],
  data() {
    return {
      save_playbook_button_clicked: false,
      prevent_click_outside_trigger: false,
      ALL_EMITTERS,
      new_node_data: null,
      source_connection_active: null,
      target_connection_active: null,
      is_mounted: false,
      playbook_updated: false,
      revert_changes: false,
      edge_hover: null,
      start_x: 450,
      start_y: 50,
      selected_node: null,
      elements: [],
      playbook: {
        id: null,
        name: null,
        is_public: true,
        description: null,
        tags: [],
        flowchart: "",
        design_chart: []
      },
      playbook_name: null,
      drawer_open: false,
      view_options: [
        { label: this.$t("Visual Editor"), value: "sequence", icon: "PlaybookSequenceIcon" },
        { label: this.$t("Code Editor"), value: "editor", icon: "PlaybookAuditIcon" },
        { label: this.$t("Executions"), value: "audit", icon: "PlaybookAuditIcon" }
      ],
      selected_view: "sequence",

      status_options: [
        { label: this.$t("Active"), value: "active", icon: "DotIcon" },
        { label: this.$t("Disabled"), value: "disabled", icon: "RedDotIcon" }
      ],
      selected_status: "active",
      filtered_company_id: null,
      prevent_changes_watch: false
    }
  },

  computed: {
    positions_map() {
      return { r: "right", l: "left", t: "top", b: "bottom" }
    },
    reverse_positions_map() {
      return { right: "r", left: "l", top: "t", bottom: "b" }
    },

    opposite_pos() {
      return { r: "l", l: "r", t: "b", b: "t" }
    },

    next_available_node_id() {
      let last_used_node_id = 0
      this.elements.forEach((el) => {
        if (parseInt(el.id) > last_used_node_id) {
          last_used_node_id = el.id
        }
      })
      return `${parseInt(last_used_node_id) + 1}`
    },
    node_dimensions() {
      return {
        branch: {
          branch: {
            w: 80,
            h: 40
          }
        },
        circle: {
          add: { w: 32, h: 32 },
          merge: { w: 32, h: 32 },
          split: { w: 32, h: 32 }
        },
        selection: {
          operator: { w: 80, h: 24 },

          status: { w: 160, h: 24 },
          extractor: { w: 280, h: 24 }
        },
        activity: {
          trigger: { w: 384, h: 128 },
          action: { w: 384, h: 128 },
          condition: { w: 384, h: 128 },
          wait: { w: 384, h: 128 },
          parallel: { w: 384, h: 128 },
          for: { w: 384, h: 128 },
          end_branch: { w: 384, h: 128 },
          end_parallel: { w: 384, h: 128 },
          end_for_loop: { w: 384, h: 128 },
          switch: { w: 384, h: 128 }
        }
      }
    }
  },
  watch: {
    elements: {
      deep: true,
      handler(oldVal, newVal) {
        if (this.is_mounted && !this.revert_changes && !this.$utils.isEqual(oldVal, newVal)) {
          if (this.isNewPlaybook) {
            this.$store.dispatch("data/savePendingPlaybook", {
              ...this.playbook,
              design_chart: this.elements
            })
          }

          if (!this.prevent_changes_watch) {
            this.playbook_updated = true
          }
        }
        if (this.revert_changes) {
          setTimeout(() => {
            this.revert_changes = false
          }, 1000)
        }
      }
    },

    drawer_open() {
      if (!this.drawer_open) {
        setTimeout(() => {
          this.selected_node = null
        }, 500)
      }
    },

    selected_status() {
      this.$store.dispatch("playbook/updatePlaybook", {
        id: this.playbook.id,
        data: { is_active: this.selected_status == "active" ? true : false }
      })
    }
  },
  /** when to create new flows
      1- break operator bond between triggers
   */
  mounted() {
    setTimeout(() => {
      this.is_mounted = true
    }, 1000)
  },
  activated() {
    this.register_listeners()
    this.init()
  },
  deactivated() {
    this.unregister_listeners()
  },
  created() {
    this.register_listeners()
    this.init()
    this.addStartNode()
    this.$store.dispatch("data/initPlaybookTriggerOptions")
  },

  methods: {
    init() {
      this.prevent_changes_watch = true
      if (!this.isNewPlaybook) {
        this.$store.dispatch("playbook/getPlaybook", this.$route.params.id).then((playbook) => {
          this.selected_status = playbook.is_active ? "active" : "disabled"
          this.playbook = playbook
          this.playbook_name = playbook.name

          let elements_calc = this.injectEvents(playbook.design_chart)

          elements_calc = this.injectEvents(playbook.design_chart)

          elements_calc = this.untwistWhatsTwisted(elements_calc)

          this.elements = elements_calc

          this.filtered_company_id = playbook.company_id

          setTimeout(() => {
            this.prevent_changes_watch = false
          }, 400)
        })
      } else {
        if (this.$store.state.data.pending_playbook) {
          this.playbook = this.$store.state.data.pending_playbook
          this.elements = this.injectEvents(this.playbook.design_chart)
        } else {
          this.playbook = {
            id: null,
            name: this.$route.query.name,
            is_public: this.$route.query.is_public,
            description: this.$route.query.description,
            tags: this.$route.query.tags,
            flowchart: "",
            design_chart: []
          }

          setTimeout(() => {
            this.prevent_changes_watch = false
          }, 400)
        }
      }
    },
    handleSelectItem(item, pos) {
      this.new_node_data.addNewNodeFunc({
        ...item,
        node_id: this.new_node_data.id,
        position: pos
      })
    },
    handleAddNewNode(data, id, addNewNodeFunc) {
      this.new_node_data = { data, id, addNewNodeFunc }
      this.prevent_click_outside_trigger = true
      setTimeout(() => {
        this.prevent_click_outside_trigger = false
      }, 500)
    },
    handleDrag() {
      this.playbook_updated = true
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.PLAYBOOK_EDITED, (data) => {
        //Update playbook with new data
        this.playbook["name"] = data.name
        this.playbook["is_public"] = data.is_public
        this.playbook["description"] = data.description
        this.playbook["tags"] = data.tags
        this.$store
          .dispatch("playbook/updatePlaybook", {
            id: this.playbook.id,
            data: data
          })
          .then(() => {})
          .catch((err) => {
            if (err?.code == "BAD_REQUEST") {
              let error_msg
              Object.keys(err.errors).forEach((key) => {
                error_msg = error_msg + " " + err.errors[key] + "<br>"
              })
              this.$toast.open({
                message: error_msg,
                position: "top",
                type: "error",
                duration: 3000
              })
            }
          })
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.PLAYBOOK_EDITED)
    },
    cancelEditPlaybook() {
      this.$store.dispatch("playbook/getPlaybook", this.$route.params.id).then((playbook) => {
        this.playbook_updated = false
        this.revert_changes = true
        this.selected_status = playbook.is_active ? "active" : "disabled"
        this.playbook = playbook
        this.playbook_name = playbook.name

        this.elements = this.injectEvents(playbook.design_chart)

        this.elements = this.untwistWhatsTwisted(this.elements)

        this.filtered_company_id = playbook.company_id
      })
    },
    handleChangeEditor(workflow) {
      this.playbook.flowchart = workflow
    },
    injectEvents(elements) {
      const elements_list_calc = []
      elements.forEach((el) => {
        if (el.is_edge) {
          elements_list_calc.push({
            ...el,
            events: {
              deleteEdgeClick: ($event) => {
                this.handleDeleteEdge($event)
              }
            }
          })
        } else {
          elements_list_calc.push({
            ...el,
            events: {
              addNewItem: (e) => {
                this.handleAddNewItem(e)
              },
              click: (e) => {
                this.handleClickNode(e.node)
              },
              deleteClick: ($event) => {
                this.handleDeleteNode($event)
              },
              activateSourceConnection: (pos) => {
                if (this.source_connection_active) {
                  this.source_connection_active = null
                } else {
                  this.source_connection_active = {
                    ...this.elements.find((ell) => ell.id == el.id),
                    pos: pos
                  }
                }
              },
              activateTargetConnection: (pos) => {
                this.handleActivateTargetConnection(pos, el.id)
              },

              hoverOverTargetConnection: (pos) => {
                this.handleHoverOverTargetConnection(pos, el.id)
              },
              hoverAwayTargetConnection: () => {
                if (this.elements.find((ell) => ell.source == this.source_connection_active.id && ell.target == el.id)?.temp_line) {
                  const index = this.elements.findIndex((ell) => ell.source == this.source_connection_active.id && ell.target == el.id)
                  this.elements.splice(index, 1)
                }
              }
            }
          })
        }
      })

      return elements_list_calc
    },
    saveBtnClicked() {
      this.save_playbook_button_clicked = true
      this.playbook.design_chart = this.twistAbit(this.$utils.clone(this.elements))

      if (this.isNewPlaybook || this.$route.path.includes("/new")) {
        this.$store
          .dispatch("playbook/createPlaybook", this.playbook)
          .then((playbook) => {
            this.$store.dispatch("data/savePendingPlaybook", null)

            this.$router.push(`/playbooks/${playbook.id}`)
          })
          .catch((err) => {
            if (err?.code == "BAD_REQUEST") {
              let error_msg = ""
              Object.keys(err.errors).forEach((key) => {
                // if err.errors[key] is list we join it with a space
                if (Array.isArray(err.errors[key])) {
                  error_msg = error_msg + " " + err.errors[key].join(" ") + "<br>"
                } else {
                  error_msg = error_msg + " " + err.errors[key] + "<br>"
                }
              })
              this.$toast.open({
                message: error_msg,
                position: "top",
                type: "error",
                duration: 3000
              })
            }
          })
      } else {
        this.$store
          .dispatch("playbook/updatePlaybook", {
            id: this.playbook.id,
            data: this.playbook
          })
          .then(() => {
            this.playbook_updated = false
            this.$toast.open({
              message: this.$t("Changes saved successfully"),
              position: "top",
              type: "success",
              duration: 3000
            })
          })
          .catch((err) => {
            if (err?.code == "BAD_REQUEST") {
              let error_msg = ""
              Object.keys(err.errors).forEach((key) => {
                // if err.errors[key] is list we join it with a space
                if (Array.isArray(err.errors[key])) {
                  error_msg = error_msg + " " + err.errors[key].join(" ") + "<br>"
                } else {
                  error_msg = error_msg + " " + err.errors[key] + "<br>"
                }
              })
              this.$toast.open({
                message: error_msg,
                position: "top",
                type: "error",
                duration: 3000
              })
            }
          })
      }
    },
    twistAbit(elements) {
      const final_twisted = []
      elements.forEach((el) => {
        const data = this.$utils.clone(el.data)

        if (el.data.type == "wait") {
          data.values["sub_item"] = "sleep"

          if (data.values.selected_delay_duration == "weeks") {
            data.values["original_selected_delay_duration"] = "weeks"
            data.values.selected_delay_duration = "days"
            data.values.selected_duration_value = data.values.selected_duration_value * 7
          }
          if (data.values.selected_delay_duration == "months") {
            data.values["original_selected_delay_duration"] = "months"
            data.values.selected_delay_duration = "days"
            data.values.selected_duration_value = data.values.selected_duration_value * 30
          }
          data.values["duration"] = `P${
            data.values.selected_delay_duration == "days"
              ? `${data.values.selected_duration_value}D`
              : data.values.selected_delay_duration == "hours"
                ? `T${data.values.selected_duration_value}H`
                : data.values.selected_delay_duration == "minutes"
                  ? `T${data.values.selected_duration_value}M`
                  : data.values.selected_delay_duration == "seconds"
                    ? `T${data.values.selected_duration_value}S`
                    : ``
          }`
          data.type = "sleep"

          delete data.values.selected_delay_duration
          delete data.values.selected_duration_value
          delete data.values.action_name
        }

        if (data.values?.condition) {
          data.values["original_condition"] = data.values?.condition
          data.values["condition"] = data.values?.condition.replaceAll("_parent", "__parent").replaceAll("__", ".")
        }

        if (data.values?.filter) {
          data.values["original_filter"] = data.values?.filter
          data.values["filter"] = data.values?.filter.replaceAll("_parent", "__parent").replaceAll("__", ".")
        }

        if (data.values?.sub_item == "filter_activities") {
          if (data.values.filter.includes("goal")) {
            data.values["sub_item"] = "filter_goals"
          }
          if (data.values.filter.includes("task")) {
            data.values["sub_item"] = "filter_tasks"
          }
          if (data.values.filter.includes("plan")) {
            data.values["sub_item"] = "filter_plans"
          }
          if (data.values.filter.includes("interaction")) {
            data.values["sub_item"] = "filter_interactions"
          }
        }

        const conditions = elements.filter((c) => c.data.type == "condition")

        if (data.type == "condition") {
          const index = conditions.findIndex((c) => c.data.values.condition == data.values.condition)
          data.values["order"] = index + 1
        }

        if (
          data.values?.sub_item == "update_goal" ||
          data.values?.sub_item == "update_task" ||
          data.values?.sub_item == "update_plan" ||
          data.values?.sub_item == "update_interaction" ||
          data.values?.sub_item == "update_milestone"
        ) {
          data.values[data.values?.sub_item.split("_")[1]] = data.values.record
          const metric_filter_clean = data.values?.metric_filter.replace("( ", "").replace(" )", "")
          const full_action_name_and_field = metric_filter_clean.split(" ")[0]
          const action_field = full_action_name_and_field.split("__")[1]
          data.values["field"] = action_field
          data.values["value"] = metric_filter_clean
            .split(" ")
            .slice(2, metric_filter_clean.split(" ").length)
            .join(" ")
            .replaceAll("'", "")

          if (data.values["field"] == "collaborators" || data.values["field"] == "assigned_to") {
            data.values["value"] = data.values["value"].split(",").map((v) => `'${v}'`)
          }
        }
        if (data.values?.sub_item == "change_metric") {
          const metric_filter_clean = data.values?.metric_filter.replace("( ", "").replace(" )", "")
          data.values["metric"] = metric_filter_clean.split(" ")[0].replaceAll("_parent", "__parent").replaceAll("__", ".")

          data.values["value"] = metric_filter_clean
            .split(" ")
            .slice(2, metric_filter_clean.split(" ").length)
            .join(" ")
            .replaceAll("'", "")

          delete el.data.values.metric_filter
        }

        if (el.data?.values?.trigger_name || el.data.type == "trigger") {
          const trigger_name_calc = el.data?.values?.trigger_name
            ? el.data?.values?.trigger_name
            : this.$utils.capitalize(data.values.result.replaceAll("_", " "))
          data.values["name"] = trigger_name_calc
          data.values["result"] = this.$utils.slugify(trigger_name_calc)
          delete el.data?.values?.trigger_name
          if (el.data.values.action_name) {
            delete el.data?.values?.action_name
          }
        }

        if (el.data?.type == "for") {
          data.values["name"] = el.data?.values?.action_name
          data.values["value"] = this.$utils.slugify(el.data?.values?.action_name)
          data.values["in"] = el.data?.values?.records
          data.values["sub_item"] = "for"
        }

        if (el.data?.values?.action_name || el.data.type == "action") {
          const action_name_calc = el.data?.values?.action_name
            ? el.data?.values?.action_name
            : this.$utils.capitalize(data.values.result.replaceAll("_", " "))
          if (
            data.values?.sub_item !== "create_plan_from_template" &&
            data.values?.sub_item !== "create_plan" &&
            data.values?.sub_item !== "create_goal_from_template" &&
            data.values?.sub_item !== "create_goal" &&
            data.values?.sub_item !== "create_task"
          ) {
            data.values["name"] = action_name_calc
          }
          data.values["result"] = this.$utils.slugify(action_name_calc)
          delete el.data.values.action_name
          if (el.data.values.trigger_name) {
            delete el.data?.values?.trigger_name
          }
        }
        final_twisted.push({
          ...el,
          data: data
        })
      })

      return final_twisted
    },

    untwistWhatsTwisted(elements) {
      const final_untwisted = []

      elements.forEach((el) => {
        const data = this.$utils.clone(el.data)

        if (el.data.type == "sleep") {
          data.type = "wait"
          const duration = data.values.duration

          if (duration.includes("D")) {
            data.values["selected_delay_duration"] = "days"
            data.values["selected_duration_value"] = parseInt(duration.replace("P", "").replace("D", ""))

            if (data.values.original_selected_delay_duration == "weeks") {
              data.values["selected_delay_duration"] = "weeks"
              data.values["selected_duration_value"] = data.values["selected_duration_value"] / 7
            }

            if (data.values.original_selected_delay_duration == "months") {
              data.values["selected_delay_duration"] = "months"
              data.values["selected_duration_value"] = data.values["selected_duration_value"] / 30
            }
          }
          if (duration.includes("M")) {
            data.values["selected_delay_duration"] = "minutes"
            data.values["selected_duration_value"] = parseInt(duration.replace("P", "").replace("T", "").replace("M", ""))
          }
          if (duration.includes("S")) {
            data.values["selected_delay_duration"] = "seconds"
            data.values["selected_duration_value"] = parseInt(duration.replace("P", "").replace("T", "").replace("S", ""))
          }
        }

        if (el.data.type == "trigger") {
          data.values["trigger_name"] = data.values.name
        }

        if (el.data.type == "action") {
          data.values["action_name"] = data.values.name
        }

        // if (el.data?.values?.sub_item == "change_metric") {
        //   data.values["metric_filter"] = `( ${el.data.values.metric} = '${el.data.values.value}' )`
        // }

        if (el.data?.values?.condition && el.data?.values?.original_condition) {
          data.values["condition"] = el.data?.values?.original_condition
        }

        if (el.data?.values?.filter && el.data?.values?.original_filter) {
          data.values["filter"] = el.data?.values?.original_filter
        }

        if (
          el.data?.values?.sub_item == "filter_goals" ||
          el.data?.values?.sub_item == "filter_plans" ||
          el.data?.values?.sub_item == "filter_tasks" ||
          el.data?.values?.sub_item == "filter_interactions"
        ) {
          data.values["sub_item"] = "filter_activities"
        }

        final_untwisted.push({
          ...el,
          data: data
        })
      })

      return final_untwisted
    },
    connectElements(id_1, id_2) {
      const new_edge_obj = {
        id: `e${id_1}b-${id_2}t`,
        is_edge: true,
        source: `${id_1}`,
        sourceHandle: "b",

        target: `${id_2}`,
        targetHandle: "t",

        type: "custom",
        style: {
          stroke: "black"
        },
        events: {
          deleteEdgeClick: ($event) => {
            this.handleDeleteEdge($event)
          }
        }
      }
      this.elements.push(new_edge_obj)
    },

    getElementFlowObjectType(id) {
      let links_to_previous_elements = this.elements.filter((element) => element.target == id)
      let traversed_paths_open = links_to_previous_elements.length
      let object_type_calc = null
      let object_type_full_calc = null
      const element_obj = this.elements.find((el) => el.id == id)

      if (element_obj.data?.values?.sub_item?.includes("metric")) {
        if (element_obj.data?.values?.metric_filter) {
          const metric_obj = transformServerFilterToMetricFilter(element_obj.data?.values?.metric_filter, true).filter[1]
          object_type_calc = metric_obj?.object_type
          traversed_paths_open = 0
        }
      } else if (this.$store.state.object_types.map((o) => o.type).includes(element_obj.data?.values?.sub_item)) {
        object_type_calc = element_obj.data.values.sub_item
        traversed_paths_open = 0
      }

      //while loop
      while (traversed_paths_open && traversed_paths_open < 4) {
        const prev_links_to_prev_element_calc = []
        links_to_previous_elements.forEach((link) => {
          const element_connected_to_link = this.elements.find((element) => element.id == link.source)

          const prev_links = this.elements.filter((element) => element.target == element_connected_to_link.id)
          if (!prev_links.length) {
            traversed_paths_open = traversed_paths_open - 1
          } else {
            traversed_paths_open = traversed_paths_open + (prev_links.length - 1)
            prev_links.forEach((prev_link) => {
              prev_links_to_prev_element_calc.push(prev_link)
            })
          }

          links_to_previous_elements = prev_links_to_prev_element_calc

          if (element_connected_to_link.data.values?.sub_item?.includes("metric")) {
            if (element_connected_to_link.data?.values?.metric_filter) {
              const metric_obj = transformServerFilterToMetricFilter(element_connected_to_link.data?.values?.metric_filter, true).filter[1]
              object_type_calc = metric_obj?.object_type
              traversed_paths_open = 0
            }
          } else if (this.$store.state.object_types.map((o) => o.type).includes(element_connected_to_link.data?.values?.sub_item)) {
            object_type_calc = element_connected_to_link.data.values.sub_item
            traversed_paths_open = 0
          }
        })
      }
      return { object_type: object_type_calc, object_type_full: object_type_full_calc }
    },
    handleSubmitNodeForm(e, callback) {
      const selected_node_in_elements = this.elements.find((el) => el.id == this.selected_node.id)

      selected_node_in_elements.data.values = e
      selected_node_in_elements.data["flowObjectType"] = this.getElementFlowObjectType(this.selected_node.id).object_type
      selected_node_in_elements.data["flowObjectTypeFull"] = this.getElementFlowObjectType(this.selected_node.id).object_type_full

      const elements_clone = this.$utils.clone(this.elements)

      this.elements = []

      setTimeout(() => {
        this.elements = elements_clone
      }, 1)

      callback()
    },
    handleUpdateReferences(params) {
      const { oldReference, newReference, nodeId } = params
      // Update all subsequent nodes that reference the old action name
      this.elements.forEach((element) => {
        // Skip the current node being edited
        if (element.id === nodeId) {
          return
        }

        // Update references in all string values within the element
        if (element.data?.values) {
          this.updateStringReferencesInObject(element.data.values, oldReference, newReference)
        }
      })

      // Mark playbook as updated
      this.playbook_updated = true
    },

    updateStringReferencesInObject(obj, oldReference, newReference) {
      // Recursively update string references in an object
      for (const key in obj) {
        if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key])) {
          // Recursively handle nested objects
          this.updateStringReferencesInObject(obj[key], oldReference, newReference)
        } else if (Array.isArray(obj[key])) {
          // Handle arrays
          obj[key].forEach((item, index) => {
            if (typeof item === "string") {
              obj[key][index] = this.updateStringReference(item, oldReference, newReference)
            } else if (typeof item === "object" && item !== null) {
              this.updateStringReferencesInObject(item, oldReference, newReference)
            }
          })
        } else if (typeof obj[key] === "string") {
          // Update string values that contain references
          obj[key] = this.updateStringReference(obj[key], oldReference, newReference)
        }
      }
    },

    updateStringReference(str, oldReference, newReference) {
      if (!str || typeof str !== "string") {
        return str
      }

      // Update references like ${old_reference.id} to ${new_reference.id}
      const oldReferencePattern = new RegExp(`\\$\\{${oldReference}(\\.[^}]*)?\\}`, "g")
      return str.replace(oldReferencePattern, (match, property) => {
        return `\${${newReference}${property || ""}}`
      })
    },
    handleAddNewItem(e) {
      this.new_node_data = null
      let target_element = e.node_id
      if (!e.position) {
        target_element = this.elements.find((el) => el.data.type == "add").id
      }

      let values = {}
      if (e.sub_item) {
        values["sub_item"] = e.sub_item
      }

      if (e.value == "condition") {
        values["condition_value"] = e.condition_value
      }

      const target_element_obj = this.elements.find((el) => el.id == target_element)
      let arrow_connection = true
      if (target_element_obj?.data?.type == "trigger" && e?.value == "trigger") {
        arrow_connection = false
      }
      if (e.position) {
        const new_node = this.addNode(
          target_element,
          e.node_type,
          e.value,
          this.positions_map[e.position],
          20,
          e.position,
          this.opposite_pos[e.position],
          arrow_connection,
          values
        )

        if (
          e.value !== "end_sequence" &&
          e.value !== "end_branch" &&
          e.value !== "end" &&
          e.value !== "end_parallel" &&
          e.value !== "end_for_loop" &&
          e.value !== "switch" &&
          e.value !== "parallel" &&
          e.value !== "add"
        ) {
          this.selected_node = {
            ...new_node,
            data: {
              ...new_node.data,
              flowObjectType: this.getElementFlowObjectType(target_element, true).object_type,
              flowObjectTypeFull: this.getElementFlowObjectType(target_element, true).object_type_full
            }
          }

          const index = this.elements.findIndex((el) => el.id == new_node.id)
          this.elements.splice(index, 1, this.selected_node)
          this.drawer_open = true
        }
      } else {
        const new_node = this.addNode(target_element, e.node_type, e.value, "bottom", 5, "b", "t", null, values)

        if (
          e.value !== "end_sequence" &&
          e.value !== "end_parallel" &&
          e.value !== "end_for_loop" &&
          e.value !== "end_branch" &&
          e.value !== "end" &&
          e.value !== "switch" &&
          e.value !== "parallel" &&
          e.value !== "add"
        ) {
          this.selected_node = {
            ...new_node,
            data: {
              ...new_node.data,
              flowObjectType: this.getElementFlowObjectType(new_node.id, true).object_type,
              flowObjectTypeFull: this.getElementFlowObjectType(new_node.id, true).object_type_full
            }
          }

          const index = this.elements.findIndex((el) => el.id == this.selected_node)
          this.elements.splice(index, 1, this.selected_node)

          this.drawer_open = true
        }
      }
    },

    handleClickNode(node) {
      if (
        node.type == "activity" &&
        node.data.type !== "end_sequence" &&
        node.data.type !== "end_branch" &&
        node.data.type !== "end" &&
        node.data.type !== "end_parallel" &&
        node.data.type !== "end_for_loop" &&
        node.data.type !== "switch" &&
        node.data.type !== "parallel" &&
        node.data.type !== "add"
      ) {
        this.selected_node = {
          ...node,
          data: {
            ...node.data
          }
        }
        const index = this.elements.findIndex((el) => el.id == this.selected_node.id)
        this.elements.splice(index, 1, this.selected_node)
        this.drawer_open = true
      }
    },

    handleEdgeMouseOver(e) {
      this.edge_hover = e.edge
    },
    handleDeleteNode(id) {
      /**
      Cases:
      1. Deleting an operator:
          - if operator after first trigger is removed:
              -> remove link to merge of first trigger
              -> assign first trigger to new flow
              -> connection of second trigger to merge from left
              -> move trigger and all following components to middle of connected triggers
          - if operator before last trigger is removed:
              -> remove link to merge of last trigger
              -> assign last trigger to new flow
              -> connection of before last trigger from right
              -> move trigger to middle of connected triggers
          - if operator from middle is deleted:
              -> remove links to merge with all triggers after operator
              -> link merge from right with last trigger before operator removed
              -> move merge to middle of connected triggers before removed operator
              -> create a new merge between all triggers after operator removed
              -> move all following components in the middle of left group of triggers
          - if operator from middle of 2 triggers removed:
              -> remove merge and links of both triggers with merge
              -> link next component after merge with first trigger
              -> move next component and all following components below first trigger
      2. Deleting a trigger:
          -> delete operators before and after it first then delete trigger
      */

      //element removed is an operator
      if (this.elements.find((element) => element.id == id)?.data?.type == "operator") {
        let operator_removed_positions = []

        /**
          [element_before_trigger_before_operator_removed (may not exist)]----[trigger_before_operator_removed]----[operater_removed]----[trigger_after_operator_removed]----[element_after_trigger_after_operator_removed (may not exist)]
         */
        const trigger_before_operator_removed = this.getConnectedSourceElements(id)[0]

        const trigger_after_operator_removed = this.getConnectedElements(id)[0]

        const elements_before_trigger_before_operator_removed = this.getConnectedSourceElements(trigger_before_operator_removed.id)
        const elements_before_trigger_before_operator_removed_has_operator = elements_before_trigger_before_operator_removed.find(
          (el) => el.data.type == "operator"
        )

        const elements_after_trigger_after_operator_removed = this.getConnectedElements(trigger_after_operator_removed.id)
        const elements_after_trigger_after_operator_removed_has_operator = elements_after_trigger_after_operator_removed.find(
          (el) => el.data.type == "operator"
        )

        if (!elements_before_trigger_before_operator_removed_has_operator) {
          operator_removed_positions.push("after_first_trigger")
        }
        if (!elements_after_trigger_after_operator_removed_has_operator) {
          operator_removed_positions.push("before_last_trigger")
        }
        if (elements_before_trigger_before_operator_removed_has_operator && elements_after_trigger_after_operator_removed_has_operator) {
          operator_removed_positions.push("middle")
        }

        let operator_removed_position
        if (operator_removed_positions.length == 2) {
          operator_removed_position = "between_two_triggers"
        } else {
          operator_removed_position = operator_removed_positions[0]
        }

        //------Start logic to find merge element
        let trigger_calc
        if (trigger_before_operator_removed) {
          trigger_calc = trigger_before_operator_removed
        } else {
          trigger_calc = trigger_after_operator_removed
        }

        let merge_element
        const all_links_of_trigger_calc = this.elements.filter(
          (element) => element.source == trigger_calc.id && element.sourceHandle == "b"
        )
        all_links_of_trigger_calc.forEach((link_element) => {
          const link_element_target_id = link_element.target
          const target_element = this.elements.find((element) => element.id == link_element_target_id)
          if (target_element?.data?.type == "split") {
            const all_links_from_split = this.elements.filter((element) => element.source == target_element.id)
            all_links_from_split.forEach((l) => {
              const element_connected = this.elements.find((el) => el.id == l.target)
              if (element_connected.data.type == "merge") {
                merge_element = element_connected
              }
            })
          } else if (target_element?.data?.type == "merge") {
            merge_element = target_element
          }
        })
        //------End logic to find merge element

        if (operator_removed_position == "after_first_trigger") {
          const bottom_link_from_trigger_to_next_element = this.elements.find(
            (element) => element.source == trigger_before_operator_removed.id && element.sourceHandle == "b"
          )
          const element_connected_below_trigger = this.elements.find((el) => el.id == bottom_link_from_trigger_to_next_element.target)

          let link_to_merge_of_first_trigger_index

          if (element_connected_below_trigger.data.type == "split") {
            link_to_merge_of_first_trigger_index = this.elements.findIndex(
              (element) => element.source == element_connected_below_trigger.id && element.target == merge_element.id
            )
            const links_from_split = this.elements.filter(
              (el) => el.source == element_connected_below_trigger.id && el.target !== merge_element.id
            )

            if (links_from_split.length <= 1) {
              const element_connected_to_split = this.elements.find((el) => el.id == links_from_split[0].target)
              bottom_link_from_trigger_to_next_element.target = element_connected_to_split.id
              this.handleDeleteNode(element_connected_below_trigger.id)
            } else if (links_from_split.length > 1) {
              let link_to_change = links_from_split[0]
              links_from_split.forEach((link) => {
                if (link.sourceX > link_to_change.sourceX) {
                  link_to_change = link
                }
              })

              link_to_change.sourceHandle = "r"
              this.elements.splice(link_to_merge_of_first_trigger_index, 1)
            }
          } else {
            link_to_merge_of_first_trigger_index = this.elements.findIndex(
              (element) => element.source == trigger_before_operator_removed.id && element.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_first_trigger_index, 1)
          }

          let link_with_trigger_after_operator_removed_and_merge = this.elements.find(
            (el) => el.source == trigger_after_operator_removed.id && el.target == merge_element.id
          )

          const bottom_link_from_trigger_after_operator_removed = this.elements.find(
            (el) => el.source == trigger_after_operator_removed.id && el.sourceHandle == "b"
          )
          const bottom_element_from_trigger_after_operator_removed = this.elements.find(
            (el) => el.id == bottom_link_from_trigger_after_operator_removed.target
          )

          if (bottom_element_from_trigger_after_operator_removed.data.type == "split") {
            link_with_trigger_after_operator_removed_and_merge = this.elements.find(
              (el) => el.source == bottom_element_from_trigger_after_operator_removed.id && el.target == merge_element.id
            )
          }

          link_with_trigger_after_operator_removed_and_merge.targetHandle = "l"
        }

        if (operator_removed_position == "before_last_trigger") {
          const bottom_link_from_trigger_to_next_element = this.elements.find(
            (element) => element.source == trigger_after_operator_removed.id && element.sourceHandle == "b"
          )
          const element_connected_below_trigger = this.elements.find((el) => el.id == bottom_link_from_trigger_to_next_element.target)

          let link_to_merge_of_last_trigger_index

          if (element_connected_below_trigger.data.type == "split") {
            link_to_merge_of_last_trigger_index = this.elements.findIndex(
              (element) => element.source == element_connected_below_trigger.id && element.target == merge_element.id
            )
            const links_from_split = this.elements.filter(
              (el) => el.source == element_connected_below_trigger.id && el.target !== merge_element.id
            )

            if (links_from_split.length <= 1) {
              const element_connected_to_split = this.elements.find((el) => el.id == links_from_split[0].target)
              bottom_link_from_trigger_to_next_element.target = element_connected_to_split.id
              this.handleDeleteNode(element_connected_below_trigger.id)
            } else if (links_from_split.length > 1) {
              let link_to_change = links_from_split[0]
              links_from_split.forEach((link) => {
                if (link.sourceX < link_to_change.sourceX) {
                  link_to_change = link
                }
              })

              link_to_change.sourceHandle = "l"
              this.elements.splice(link_to_merge_of_last_trigger_index, 1)
            }
          } else {
            link_to_merge_of_last_trigger_index = this.elements.findIndex(
              (element) => element.source == trigger_after_operator_removed.id && element.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_last_trigger_index, 1)
          }

          let link_with_trigger_before_operator_removed_and_merge = this.elements.find(
            (el) => el.source == trigger_before_operator_removed.id && el.target == merge_element.id
          )

          const bottom_link_from_trigger_before_operator_removed = this.elements.find(
            (el) => el.source == trigger_before_operator_removed.id && el.sourceHandle == "b"
          )
          const bottom_element_from_trigger_before_operator_removed = this.elements.find(
            (el) => el.id == bottom_link_from_trigger_before_operator_removed.target
          )

          if (bottom_element_from_trigger_before_operator_removed.data.type == "split") {
            link_with_trigger_before_operator_removed_and_merge = this.elements.find(
              (el) => el.source == bottom_element_from_trigger_before_operator_removed.id && el.target == merge_element.id
            )
          }

          link_with_trigger_before_operator_removed_and_merge.targetHandle = "r"
        }

        if (operator_removed_position == "between_two_triggers") {
          const bottom_link_from_trigger_after_operator_to_next_element = this.elements.find(
            (el) => el.source == trigger_after_operator_removed.id && el.sourceHandle == "b"
          )
          const element_below_trigger_after_operator = this.elements.find(
            (el) => el.id == bottom_link_from_trigger_after_operator_to_next_element.target
          )
          if (element_below_trigger_after_operator.data.type == "split") {
            const link_to_merge_of_trigger_after_operator_removed_index = this.elements.findIndex(
              (el) => el.source == element_below_trigger_after_operator.id && el.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_trigger_after_operator_removed_index, 1)
            this.removeSplitOrRearrangeItsConnections(element_below_trigger_after_operator)
          } else {
            const link_to_merge_of_trigger_after_operator_removed_index = this.elements.findIndex(
              (element) => element.source == trigger_after_operator_removed.id && element.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_trigger_after_operator_removed_index, 1)
          }

          const bottom_link_from_trigger_before_operator_to_next_element = this.elements.find(
            (el) => el.source == trigger_before_operator_removed.id && el.sourceHandle == "b"
          )
          const element_below_trigger_before_operator = this.elements.find(
            (el) => el.id == bottom_link_from_trigger_before_operator_to_next_element.target
          )
          if (element_below_trigger_before_operator.data.type == "split") {
            const link_to_merge_of_trigger_before_operator_removed_index = this.elements.findIndex(
              (el) => el.source == element_below_trigger_before_operator.id && el.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_trigger_before_operator_removed_index, 1)
            this.removeSplitOrRearrangeItsConnections(element_below_trigger_before_operator)
          } else {
            const link_to_merge_of_trigger_before_operator_removed_index = this.elements.findIndex(
              (element) => element.source == trigger_before_operator_removed.id && element.target == merge_element.id
            )
            this.elements.splice(link_to_merge_of_trigger_before_operator_removed_index, 1)
          }
          this.handleDeleteNode(merge_element.id)

          // this.elements = this.elements.filter(element=>element.id ==  merge_element.id)
        }

        if (operator_removed_position == "middle") {
          let link_of_trigger_before_operator_with_merge

          const bottom_link_of_trigger_before_operator = this.elements.find(
            (el) => el.source == trigger_before_operator_removed.id && el.sourceHandle == "b"
          )

          const element_connected_to_bottom = this.elements.find((el) => el.id == bottom_link_of_trigger_before_operator.target)
          if (element_connected_to_bottom.data.type == "split") {
            link_of_trigger_before_operator_with_merge = this.elements.find(
              (element) => element.source == element_connected_to_bottom.id && element.target == merge_element.id
            )
            link_of_trigger_before_operator_with_merge.targetHandle = "r"

            const split_connections_with_other_nodes = this.elements.filter(
              (el) => el.source == element_connected_to_bottom.id && el.target !== merge_element.id
            )

            const left_split_connections_with_other_nodes = split_connections_with_other_nodes.filter((l) => l.sourceHandle == "l")

            if (split_connections_with_other_nodes.length) {
              if (split_connections_with_other_nodes.length > 1) {
                left_split_connections_with_other_nodes.forEach((link) => {
                  link.sourceHandle = "b"
                })
                let link_of_last_node_connected = split_connections_with_other_nodes[0]
                split_connections_with_other_nodes.forEach((link) => {
                  if (link.sourceX > link_of_last_node_connected.sourceX) {
                    link_of_last_node_connected = link
                  }
                })
                link_of_last_node_connected.sourceHandle = "r"
              } else {
                left_split_connections_with_other_nodes[0].sourceHandle = "r"
              }
            }

            link_of_trigger_before_operator_with_merge.sourceHandle = "l"
          } else {
            link_of_trigger_before_operator_with_merge = this.elements.find(
              (element) => element.source == trigger_before_operator_removed.id && element.target == merge_element.id
            )
            link_of_trigger_before_operator_with_merge.targetHandle = "r"
          }

          const trigger_ids_to_unlink_from_merge = []

          trigger_ids_to_unlink_from_merge.push(trigger_after_operator_removed.id)
          let last_trigger_found = false

          let next_trigger = trigger_after_operator_removed
          while (!last_trigger_found) {
            next_trigger = this.getNextConnectedTrigger(next_trigger.id)
            if (next_trigger) {
              trigger_ids_to_unlink_from_merge.push(next_trigger.id)
            } else {
              last_trigger_found = true
            }
          }

          trigger_ids_to_unlink_from_merge.forEach((trigger_id) => {
            const bottom_link = this.elements.find((el) => el.source == trigger_id && el.sourceHandle == "b")
            const bottom_element = this.elements.find((el) => el.id == bottom_link.target)

            if (bottom_element.data.type == "split") {
              const link_with_merge_index = this.elements.findIndex((el) => el.source == bottom_element.id && el.target == merge_element.id)
              this.elements.splice(link_with_merge_index, 1)

              const remaining_links = this.elements.filter((el) => el.source == bottom_element.id)

              if (remaining_links.length == 1) {
                remaining_links[0].source = trigger_id
                remaining_links[0].sourceHandle = "b"
                this.handleDeleteNode(bottom_element.id)
              }
            } else {
              const link_of_trigger_with_merge_index = this.elements.findIndex(
                (element) => element.source == trigger_id && element.target == merge_element.id
              )
              this.elements.splice(link_of_trigger_with_merge_index, 1)
            }
          })

          this.addMerge(trigger_ids_to_unlink_from_merge, 2)
        }
      }

      if (this.elements.find((element) => element.id == id).data?.type == "trigger") {
        if (this.getConnectedElements(id, "r")[0]?.data?.type == "operator") {
          this.handleDeleteNode(this.getConnectedElements(id, "r")[0].id)
        }

        const link_to_previous_element = this.elements.find((el) => el.target == id && el.targetHandle == "l")
        let previous_element_before_trigger
        if (link_to_previous_element) {
          previous_element_before_trigger = this.elements.find((el) => el.id == link_to_previous_element.source)
        }
        if (previous_element_before_trigger && previous_element_before_trigger.data.type == "operator") {
          this.handleDeleteNode(previous_element_before_trigger.id)
        }
      }

      if (this.elements.find((element) => element.id == id).is_in_parallel_flow) {
        const next_connected_element = this.getConnectedElements(id)[0]

        if (
          next_connected_element.data.type == "end_parallel" &&
          this.getConnectedSourceElements(next_connected_element.id).length == 1 &&
          this.getConnectedSourceElements(id)[0].data.type == "parallel"
        ) {
          this.handleDeleteNode(next_connected_element.id)
        }

        if (
          (next_connected_element.data.type == "end_parallel" || next_connected_element.is_in_parallel_flow) &&
          this.getConnectedSourceElements(id)[0].is_in_parallel_flow
        ) {
          const id_1 = this.getConnectedSourceElements(id)[0].id
          const id_2 = next_connected_element.id
          setTimeout(() => {
            this.connectElements(id_1, id_2)
          }, 1000)
        }
      }

      if (this.elements.find((element) => element.id == id).data.node_in_loop) {
        const next_connected_element = this.getConnectedElements(id)[0]

        if (next_connected_element.data.type == "end_for_loop" && this.getConnectedSourceElements(id)[0].data?.type == "for") {
          this.handleDeleteNode(next_connected_element.id)
        }

        if (next_connected_element.data.type == "end_for_loop" || next_connected_element.is_in_parallel_flow) {
          const id_1 = this.getConnectedSourceElements(id)[0].id
          const id_2 = next_connected_element.id
          setTimeout(() => {
            this.connectElements(id_1, id_2)
          }, 1000)
        }
      }

      const links_out_of_element = this.elements.filter((el) => el.source == id)
      const links_into_element = this.elements.filter((el) => el.target == id)

      const merges_to_reposition = []
      const splits_to_reposition = []
      links_out_of_element.forEach((link) => {
        const element_connected_to_link = this.elements.find((el) => el.id == link.target)
        if (element_connected_to_link.data.type == "merge") {
          if (!merges_to_reposition.find((m) => m.id == element_connected_to_link.id)) {
            merges_to_reposition.push(element_connected_to_link)
          }
        }
      })

      links_into_element.forEach((link) => {
        const element_connected_to_link = this.elements.find((el) => el.id == link.source)
        if (element_connected_to_link.data.type == "split") {
          if (!splits_to_reposition.find((m) => m.id == element_connected_to_link.id)) {
            splits_to_reposition.push(element_connected_to_link)
          }
        }
      })

      this.elements = this.elements.filter((element) => element.id !== id && element.source !== id && element.target !== id)

      merges_to_reposition.forEach((merge) => {
        this.removeMergeOrRearrangeItsConnections(merge)
      })

      splits_to_reposition.forEach((split) => {
        this.removeSplitOrRearrangeItsConnections(split)
      })
    },

    handleDeleteEdge(edge_element) {
      const source_element = this.elements.find((el) => el.id == edge_element.source)
      const target_element = this.elements.find((el) => el.id == edge_element.target)
      this.elements = this.elements.filter((el) => !(el.source == edge_element.source && el.target == edge_element.target))

      if (source_element?.data?.type == "split") {
        this.removeSplitOrRearrangeItsConnections(source_element)
      }
      if (target_element?.data?.type == "merge") {
        this.removeMergeOrRearrangeItsConnections(target_element)
      }
    },
    addStartNode() {
      const id_calc = this.next_available_node_id

      const start_obj = {
        id: "1",
        label: "MAIN",
        type: "branch",
        is_edge: false,
        position: { x: this.start_x, y: this.start_y },
        draggable: false,
        data: { type: "branch" },

        events: {
          addNewItem: (e) => {
            this.handleAddNewItem(e)
          },
          click: (e) => {
            this.handleClickNode(e.node)
          },
          deleteClick: ($event) => {
            this.handleDeleteNode($event)
          },
          activateSourceConnection: (pos) => {
            if (this.source_connection_active) {
              this.source_connection_active = null
            } else {
              this.source_connection_active = {
                ...this.elements.find((el) => el.id == id_calc),
                pos: pos
              }
            }
          },
          activateTargetConnection: (pos) => {
            this.handleActivateTargetConnection(pos, id_calc)
          },

          hoverOverTargetConnection: (pos) => {
            this.handleHoverOverTargetConnection(pos, id_calc)
          },
          hoverAwayTargetConnection: () => {
            if (this.elements.find((el) => el.source == this.source_connection_active.id && el.target == id_calc)?.temp_line) {
              const index = this.elements.findIndex((el) => el.source == this.source_connection_active.id && el.target == id_calc)
              this.elements.splice(index, 1)
            }
          }
        }
      }
      this.elements.push(start_obj)
    },
    //source_node_id, node_type, type,placement, connection_length, source_connection, target_connection, arrow_connection, values
    addNode(source_node_id, node_type, type, placement, connection_length, source_connection, target_connection, arrow_connection, values) {
      const source_element = this.elements.find((element) => element.id == source_node_id)

      let node_in_loop = null
      if (source_element) {
        let is_in_parallel_flow = false

        if (source_element?.data.type == "parallel" && !this.getConnectedElements(source_element.id).length) {
          is_in_parallel_flow = true

          setTimeout(() => {
            this.handleAddNewItem({
              name: this.$t("Add End"),
              node_type: "activity",
              value: "end_parallel",
              icon_bg_color: "#F9717115",
              icon: "PlaybookEndIcon",
              options: [],
              node_id: `${this.next_available_node_id - 1}`,
              position: "b",
              position_shift: "r"
            })
          }, 1400)
        }

        if (source_element?.data?.type == "for" && !this.getConnectedElements(source_element.id).length) {
          node_in_loop = this.$utils.slugify(source_element?.data?.values.action_name)
          setTimeout(() => {
            this.handleAddNewItem({
              name: this.$t("Add End"),
              node_type: "activity",
              value: "end_for_loop",
              icon_bg_color: "#F9717115",
              icon: "PlaybookEndIcon",
              options: [],
              node_id: `${this.next_available_node_id - 1}`,
              position: "b",
              position_shift: "r"
            })
          }, 1400)
        }

        let x_shift = 0
        let y_shift = 0

        if (source_element?.data.type == "parallel" && this.getConnectedElements(source_element.id).length == 1 && type !== "split") {
          x_shift = this.getConnectedElements(source_element.id)[0].position.x - source_element.position.x
          if (x_shift > 0) {
            x_shift += 400
          } else {
            x_shift = 700
          }
          is_in_parallel_flow = true
          setTimeout(() => {
            this.connectElements(
              this.next_available_node_id - 2,
              this.elements.filter((el) => el.data.type == "end_parallel")[
                this.elements.filter((el) => el.data.type == "end_parallel").length - 1
              ].id
            )
          }, 1000)
        }

        if (source_element?.data.type == "split" && this.getConnectedSourceElements(source_node_id)[0].data.type == "parallel") {
          is_in_parallel_flow = true

          x_shift =
            this.getConnectedElements(source_element.id)[this.getConnectedElements(source_element.id).length - 1].position.x -
            source_element.position.x
          if (x_shift > 0) {
            x_shift += 700
          } else {
            x_shift = 0
          }
          setTimeout(() => {
            this.connectElements(
              this.next_available_node_id - 1,
              this.elements.filter((el) => el.data.type == "end_parallel")[
                this.elements.filter((el) => el.data.type == "end_parallel").length - 1
              ].id
            )
          }, 1000)
        }

        // else if(source_element?.data.type == "parallel" && this.getConnectedElements(source_element.id).length == 1){
        //   this.connectElements(this.next_available_node_id , this.elements.filter(el=>el.data.type=='end_parallel')[this.elements.filter(el=>el.data.type=='end_parallel').length-1].id)
        // }

        if (
          source_element?.is_in_parallel_flow &&
          source_element.data.type !== "split" &&
          source_element.data.type !== "parallel" &&
          type !== "end_parallel"
        ) {
          is_in_parallel_flow = true
          const connection_to_next_or_end_index = this.elements.findIndex((el) => el.source == source_node_id)
          const next_node_id = this.elements.find((el) => el.source == source_node_id)?.targetNode?.id

          setTimeout(() => {
            this.connectElements(this.next_available_node_id - 1, next_node_id)
          }, 1000)

          this.elements.splice(connection_to_next_or_end_index, 1)
        }

        if (source_element?.node_in_loop && source_element.data?.type !== "for" && type !== "end_for_loop") {
          node_in_loop = source_element?.node_in_loop
          const connection_to_next_or_end_index = this.elements.findIndex((el) => el.source == source_node_id)
          const next_node_id = this.elements.find((el) => el.source == source_node_id)?.targetNode?.id

          setTimeout(() => {
            this.connectElements(this.next_available_node_id - 1, next_node_id)
          }, 1000)

          this.elements.splice(connection_to_next_or_end_index, 1)
        }

        const node_type_calc = node_type || "activity"
        let element_obj

        let split_to_reconnect
        let element_connected_to_source_element

        const source_component_position = source_element?.position

        const source_component_width = this.node_dimensions[source_element.type][source_element.data.type].w
        const new_component_width = this.node_dimensions[node_type_calc][type].w
        const w_diff = source_component_width / 2 - new_component_width / 2

        const source_component_height = this.node_dimensions[source_element.type][source_element.data.type].h
        const new_component_height = this.node_dimensions[node_type_calc][type].h
        const h_diff = source_component_height / 2 - new_component_height / 2

        const id_calc = this.next_available_node_id

        element_obj = {
          id: `${this.next_available_node_id}`,
          label: "",
          type: node_type_calc,
          is_edge: false,
          is_in_parallel_flow: is_in_parallel_flow,
          node_in_loop: type !== "end_for_loop" && source_element?.node_in_loop ? source_element?.node_in_loop : node_in_loop,
          draggable: type == "add" && !this.getConnectedSourceElements(this.next_available_node_id - 1).length ? false : true,
          data: {
            type: type,
            is_in_parallel_flow: is_in_parallel_flow,
            node_in_loop: type !== "end_for_loop" && source_element?.node_in_loop ? source_element?.node_in_loop : node_in_loop
          },

          events: {
            addNewItem: (e) => {
              this.handleAddNewItem(e)
            },
            click: (e) => {
              this.handleClickNode(e.node)
            },
            deleteClick: ($event) => {
              this.handleDeleteNode($event)
            },
            activateSourceConnection: (pos) => {
              if (this.source_connection_active) {
                this.source_connection_active = null
              } else {
                this.source_connection_active = {
                  ...this.elements.find((el) => el.id == id_calc),
                  pos: pos
                }
              }
            },
            activateTargetConnection: (pos) => {
              this.handleActivateTargetConnection(pos, id_calc)
            },

            hoverOverTargetConnection: (pos) => {
              this.handleHoverOverTargetConnection(pos, id_calc)
            },
            hoverAwayTargetConnection: () => {
              if (this.elements.find((el) => el.source == this.source_connection_active.id && el.target == id_calc)?.temp_line) {
                const index = this.elements.findIndex((el) => el.source == this.source_connection_active.id && el.target == id_calc)
                this.elements.splice(index, 1)
              }
            }
          }
        }

        if (type == "merge" || type == "split") {
          element_obj.data["position"] = this.reverse_positions_map[placement]
        }

        if (values) {
          element_obj["data"] = {
            ...element_obj["data"],
            values: this.$utils.clone(values)
          }
        }

        if (placement == "bottom") {
          y_shift = source_component_height / 2 + new_component_height / 2 + connection_length * 10
        }
        if (placement == "top") {
          y_shift = (source_component_height / 2 + new_component_height / 2 + connection_length * 10) * -1
        }
        ``
        if (placement == "right") {
          x_shift = source_component_width / 2 + new_component_width / 2 + connection_length * 10
        }
        if (placement == "left") {
          x_shift = (source_component_width / 2 + new_component_width / 2 + connection_length * 10) * -1
        }

        if (type == "condition") {
          const switch_or_split = this.elements.find((el) => source_node_id == el.id)
          const elements_connected_to_switch_or_split = this.elements
            .filter(
              (el) =>
                el.source == switch_or_split.id &&
                el.targetNode?.data?.type !== "end_branch" &&
                el.targetNode?.data?.type !== "end_parallel"
            )
            .map((el) => el.targetNode)

          if (elements_connected_to_switch_or_split.length) {
            const last_element_connected_x_position =
              elements_connected_to_switch_or_split[elements_connected_to_switch_or_split.length - 1].position.x

            x_shift = last_element_connected_x_position + new_component_width
          }
        }

        if (type == "add") {
          const parallel = this.elements.find((el) => source_node_id == el.id)
          const elements_connected_to_parallel = this.getConnectedElements(parallel.id)

          if (elements_connected_to_parallel.length) {
            x_shift = x_shift + 350
          } else if (parallel.data.type == "parallel") {
            x_shift = x_shift - 350
          }
        }

        if (type == "end_branch" || type == "end_parallel") {
          const connected_element = this.elements.find((el) => el.id == source_node_id)
          if (connected_element.data.type == "add") {
            x_shift = x_shift + 350
          }
        }

        element_obj["position"] = {
          x: source_component_position.x + w_diff + x_shift,
          y: source_component_position.y + h_diff + y_shift
        }

        if (split_to_reconnect) {
          const element_connected_to_source_element_start_x = element_connected_to_source_element.position.x
          const element_connected_to_source_element_end_x =
            element_connected_to_source_element.position.x +
            this.node_dimensions[element_connected_to_source_element.type][element_connected_to_source_element.data.type].w

          const element_connected_to_source_element_start_y = element_connected_to_source_element.position.y
          const element_connected_to_source_element_end_y =
            element_connected_to_source_element.position.y +
            this.node_dimensions[element_connected_to_source_element.type][element_connected_to_source_element.data.type].h

          if (placement == "bottom") {
            if (
              element_connected_to_source_element_start_x < split_to_reconnect.position.x &&
              element_connected_to_source_element_end_x > split_to_reconnect.position.x
            ) {
              const element_connected_to_source_element_x_shift =
                (element_connected_to_source_element_end_x - split_to_reconnect.position.x + 50) * -1
              element_connected_to_source_element.position.x =
                element_connected_to_source_element.position.x + element_connected_to_source_element_x_shift
            }

            const element_obj_shift = this.node_dimensions[element_obj.type][element_obj.data.type].w / 2 + 50

            if (element_connected_to_source_element_start_x > split_to_reconnect.position.x) {
              element_obj.position.x = element_obj.position.x - element_obj_shift
            } else {
              element_obj.position.x = element_obj.position.x + element_obj_shift
            }
          } else {
            if (
              element_connected_to_source_element_start_y < split_to_reconnect.position.y &&
              element_connected_to_source_element_end_y > split_to_reconnect.position.y
            ) {
              const element_connected_to_source_element_y_shift =
                (element_connected_to_source_element_end_y - split_to_reconnect.position.y + 50) * -1
              element_connected_to_source_element.position.y =
                element_connected_to_source_element.position.y + element_connected_to_source_element_y_shift
            }

            const element_obj_shift = this.node_dimensions[element_obj.type][element_obj.data.type].h / 2 + 50

            if (element_connected_to_source_element_start_y > split_to_reconnect.position.y) {
              element_obj.position.y = element_obj.position.y - element_obj_shift
            } else {
              element_obj.position.y = element_obj.position.y + element_obj_shift
            }
          }
        }

        this.elements.push(element_obj)

        if (source_connection) {
          this.source_connection_active = { ...source_element, pos: source_connection }

          this.handleHoverOverTargetConnection(target_connection, element_obj.id)

          this.handleActivateTargetConnection(target_connection, element_obj.id, arrow_connection)
        }

        return element_obj
      }
    },

    handleActivateTargetConnection(pos, id_calc, arrow_connection) {
      if (this.source_connection_active) {
        const source_connection_active_clone = this.$utils.clone(this.source_connection_active)

        this.source_connection_active = null
        this.target_connection_active = null

        const index = this.elements.findIndex(
          (el) => el.source == source_connection_active_clone.id && el.target == id_calc && el.temp_line
        )

        if (source_connection_active_clone.data.type == "parallel") {
          setTimeout(() => {
            if (
              !this.elements.find(
                (el) =>
                  el.source == id_calc &&
                  el.target ==
                    this.elements.filter((el) => el.data.type == "end_parallel")[
                      this.elements.filter((el) => el.data.type == "end_parallel").length - 1
                    ].id
              )
            ) {
              this.connectElements(
                id_calc,
                this.elements.filter((el) => el.data.type == "end_parallel")[
                  this.elements.filter((el) => el.data.type == "end_parallel").length - 1
                ].id
              )
            }

            const target_index = this.elements.findIndex((el) => el.id == id_calc)

            if (target_index !== -1) {
              this.elements[target_index].is_in_parallel_flow = true
              this.elements[target_index].data["is_in_parallel_flow"] = true
            }
          }, 2000)
        }

        const temp_link_clone = this.$utils.clone(this.elements[index])
        if (temp_link_clone) {
          this.elements.splice(index, 1)
        }

        if (temp_link_clone) {
          setTimeout(() => {
            const target_node = this.elements.find((el) => el.id == id_calc)
            if (
              source_connection_active_clone.data.type == "trigger" &&
              temp_link_clone.sourceHandle == "r" &&
              temp_link_clone.targetHandle == "l" &&
              target_node.data.type == "trigger"
            ) {
              const operator = this.addNode(source_connection_active_clone.id, "selection", "operator", "right", 5, "r", "l", false, {
                operator: "and"
              })
              const new_edge_obj = {
                id: `e${operator.id}b-${id_calc}t`,
                is_edge: true,
                source: `${operator.id}`,
                sourceHandle: "r",

                target: `${id_calc}`,
                targetHandle: "l",

                type: "custom",
                style: {
                  stroke: "black"
                },
                events: {
                  deleteEdgeClick: ($event) => {
                    this.handleDeleteEdge($event)
                  }
                }
              }
              this.elements.push(new_edge_obj)
            } else {
              const other_connections_done_to_target = this.elements.filter(
                (el) => el.target == id_calc && el.targetHandle == temp_link_clone.targetHandle
              )
              const other_connections_done_from_source = this.elements.filter(
                (el) => el.source == source_connection_active_clone.id && el.sourceHandle == temp_link_clone.sourceHandle
              )

              //source_node_id, node_type, type,placement, connection_length, source_connection, target_connection, arrow_connection, values, flow_number

              /**merge connection direction cases
              1- Connected to top of target element (ie: temp_link.targetHandle = 't')
                  -> merge placed to top
                  -> link always connected from bottom of merge to top of target element
                  -> existing connection between node and target element is from node source handle position to either right or left of merge
                  -> new connection between source connection active node and merge is from node source handle position to either right or left of merge
              2- Connected to left of target element (ie: temp_link.targetHandle = 'l')
                  -> merge placed to left
                  -> link always connected from right of merge to left of target element
                  -> existing connection between node and target element is from node source handle position to either top or bottom of merge
                  -> new connection between source connection active node and merge is from node source handle position to either top or bottom of merge
              2- Connected to right of target element (ie: temp_link.targetHandle = 'r')
                  -> merge placed to right
                  -> link always connected from left of merge to right of target element
                  -> existing connection between node and target element is from node source handle position to either top or bottom of merge
                  -> new connection between source connection active node and merge is from node source handle position to either top or bottom of merge
              */

              let merge_to_rearrange
              if (target_node.data.type !== "merge" && other_connections_done_to_target.length) {
                const new_merge = this.addNode(id_calc, "circle", "merge", this.positions_map[temp_link_clone.targetHandle], 3)

                let merge_target_handle_calc

                other_connections_done_to_target.forEach((link, i) => {
                  link.target = new_merge.id
                  const node_connected_to_link = this.elements.find((el) => el.id == link.source)

                  if (temp_link_clone.targetHandle == "t") {
                    if (node_connected_to_link.position.x < source_connection_active_clone.position.x) {
                      merge_target_handle_calc = "l"
                    } else {
                      merge_target_handle_calc = "r"
                    }
                  }

                  if (temp_link_clone.targetHandle == "l" || temp_link_clone.targetHandle == "r") {
                    if (node_connected_to_link.position.y < source_connection_active_clone.position.y) {
                      merge_target_handle_calc = "t"
                    } else {
                      merge_target_handle_calc = "b"
                    }
                  }

                  if (!i) {
                    link.targetHandle = merge_target_handle_calc
                  }
                })

                //connection between merge and target element
                const new_edge_obj = {
                  id: `e${new_merge.id}b-${id_calc}t`,
                  is_edge: true,
                  source: `${new_merge.id}`,
                  sourceHandle: this.opposite_pos[temp_link_clone.targetHandle],
                  target: `${id_calc}`,
                  targetHandle: temp_link_clone.targetHandle,
                  markerEnd: MarkerType.Arrow,
                  animated: true,
                  type: "custom",
                  style: {
                    stroke: "black"
                  },
                  events: {
                    deleteEdgeClick: ($event) => {
                      this.handleDeleteEdge($event)
                    }
                  }
                }
                this.elements.push(new_edge_obj)

                //connection between source element and merge

                const source_element_existing_connected_nodes = this.getConnectedElements(
                  source_connection_active_clone.id,
                  temp_link_clone.sourceHandle
                )

                if (!source_element_existing_connected_nodes?.length || source_connection_active_clone.data.type == "split") {
                  const new_edge_obj_2 = {
                    id: `e${source_connection_active_clone.id}b-${new_merge.id}t`,
                    is_edge: true,
                    source: `${source_connection_active_clone.id}`,
                    sourceHandle: temp_link_clone.sourceHandle,

                    target: `${new_merge.id}`,
                    targetHandle: this.opposite_pos[merge_target_handle_calc],
                    markerEnd: MarkerType.Arrow,
                    type: "custom",
                    style: {
                      stroke: "black"
                    },
                    events: {
                      deleteEdgeClick: ($event) => {
                        this.handleDeleteEdge($event)
                      }
                    }
                  }
                  this.elements.push(new_edge_obj_2)

                  if (source_connection_active_clone.data.type == "split") {
                    this.removeSplitOrRearrangeItsConnections(source_connection_active_clone)
                  }
                  this.removeMergeOrRearrangeItsConnections(new_merge)
                } else {
                  merge_to_rearrange = new_merge
                }
              }

              /**split connection direction cases
              1- Connected from bottom of source element (ie:  temp_link.sourceHandle = 'b')
                  -> merge placed to bottom
                  -> split always connected from bottom of source element to top of split
                  -> existing connection between node and split element is from either right or left of split to node target handle
                  -> new connection between split and target element is from either right or left of split to target handle position of temp link
              2- Connected from left of source element (ie:  temp_link.sourceHandle = 'l')
                  -> merge placed to left
                  -> split always connected from left of source element to right of split
                  -> existing connection between node and split element is from either top or bottom of split to node target handle
                  -> new connection between split and target element is from either top or bottom of split to target handle position of temp link
              3- Connected from right of source element (ie:  temp_link.sourceHandle = 'r')
                  -> merge placed to right
                  -> split always connected from right of source element to left of split
                  -> existing connection between node and split element is from either top or bottom of split to node target handle
                  -> new connection between split and target element is from either top or bottom of split to target handle position of temp link
              */

              if (source_connection_active_clone.data.type !== "split" && other_connections_done_from_source.length) {
                const new_split = this.addNode(
                  source_connection_active_clone.id,
                  "circle",
                  "split",
                  this.positions_map[temp_link_clone.sourceHandle],
                  3
                )

                let merge_source_handle_calc
                other_connections_done_from_source.forEach((link) => {
                  const node_connected_to_link = this.elements.find((el) => el.id == link.target)

                  if (temp_link_clone.sourceHandle == "b") {
                    if (node_connected_to_link.position.x < source_connection_active_clone.position.x) {
                      merge_source_handle_calc = "l"
                    } else {
                      merge_source_handle_calc = "r"
                    }
                  }

                  if (temp_link_clone.sourceHandle == "l" || temp_link_clone.sourceHandle == "r") {
                    if (node_connected_to_link.position.y < source_connection_active_clone.position.y) {
                      merge_source_handle_calc = "t"
                    } else {
                      merge_source_handle_calc = "b"
                    }
                  }

                  //link between split and connected element
                  const new_edge_obj = {
                    id: `e${new_split.id}b-${link.target}t`,
                    is_edge: true,
                    source: `${new_split.id}`,
                    sourceHandle: merge_source_handle_calc,

                    target: `${link.target}`,
                    targetHandle: link.targetHandle,
                    markerEnd: MarkerType.Arrow,
                    type: "custom",
                    style: {
                      stroke: "black"
                    },
                    events: {
                      deleteEdgeClick: ($event) => {
                        this.handleDeleteEdge($event)
                      }
                    }
                  }
                  this.elements.push(new_edge_obj)

                  link.target = new_split.id
                  link.sourceHandle = temp_link_clone.sourceHandle
                  link.targetHandle = this.opposite_pos[temp_link_clone.sourceHandle]
                })

                const target_source_connection = this.getConnectedSourceElements(target_node.id, temp_link_clone.targetHandle)[0]
                const target_source_connection_node_type = target_source_connection?.data?.type
                //connection between split and target element
                const new_edge_obj = {
                  id: `e${new_split.id}b-${target_source_connection_node_type ? merge_to_rearrange.id : id_calc}t`,
                  is_edge: true,
                  source: `${new_split.id}`,
                  sourceHandle: this.opposite_pos[merge_source_handle_calc],

                  target: `${target_source_connection_node_type ? merge_to_rearrange.id : id_calc}`,
                  targetHandle: temp_link_clone.targetHandle,
                  markerEnd: MarkerType.Arrow,
                  type: "custom",
                  style: {
                    stroke: "black"
                  },
                  events: {
                    deleteEdgeClick: ($event) => {
                      this.handleDeleteEdge($event)
                    }
                  }
                }
                this.elements.push(new_edge_obj)
                this.removeSplitOrRearrangeItsConnections(new_split)
                if (merge_to_rearrange) {
                  this.removeMergeOrRearrangeItsConnections(merge_to_rearrange)
                }
              }

              if (
                (source_connection_active_clone.data.type == "split" && !other_connections_done_to_target.length) ||
                target_node.data.type == "merge" ||
                (!other_connections_done_to_target.length && !other_connections_done_from_source.length)
              ) {
                const new_edge_obj = {
                  id: `e${source_connection_active_clone.id}b-${id_calc}t`,
                  is_edge: true,
                  source: `${source_connection_active_clone.id}`,
                  sourceHandle: source_connection_active_clone.pos,

                  target: `${id_calc}`,
                  targetHandle: temp_link_clone.targetHandle,
                  animated: true,

                  type: "custom",
                  style: {
                    stroke: "black"
                  },
                  events: {
                    deleteEdgeClick: ($event) => {
                      this.handleDeleteEdge($event)
                    }
                  }
                }

                if (arrow_connection) {
                  new_edge_obj["markerEnd"] = MarkerType.Arrow
                }
                this.elements.push(new_edge_obj)
                if (source_connection_active_clone.data.type == "split") {
                  this.removeSplitOrRearrangeItsConnections(source_connection_active_clone)
                }
                if (target_node.data.type == "merge") {
                  this.removeMergeOrRearrangeItsConnections(target_node)
                }
              }
            }
          }, 50)
        }
      } else {
        if (id_calc == this.target_connection_active?.id) {
          this.target_connection_active = null
        } else {
          this.target_connection_active = {
            ...this.elements.find((el) => el.id == id_calc),
            pos: pos
          }
        }
      }
    },

    handleHoverOverTargetConnection(pos, id) {
      if (this.source_connection_active && !this.elements.find((el) => el.source == this.source_connection_active.id && el.target == id)) {
        const new_temp_edge_obj = {
          id: `temp-${this.source_connection_active.id}-${id}`,
          is_edge: true,
          source: `${this.source_connection_active.id}`,
          sourceHandle: this.source_connection_active.pos,

          target: `${id}`,
          targetHandle: pos,
          animated: true,
          temp_line: true,
          type: "custom",
          style: {
            stroke: "green"
          }
        }

        this.elements.push(new_temp_edge_obj)
      }
    },

    removeSplitOrRearrangeItsConnections(split_element) {
      const elements_connected_to_split = this.getConnectedElements(split_element.id)
      const link_between_split_and_source_element = this.elements.find((el) => el.target == split_element.id)
      const split_connection_direction = split_element.data.position
      if (elements_connected_to_split.length == 1) {
        if (link_between_split_and_source_element) {
          link_between_split_and_source_element.target = elements_connected_to_split[0].id
        }
        this.handleDeleteNode(split_element.id)
      } else if (elements_connected_to_split.length > 1) {
        let first_element = elements_connected_to_split[0]
        let last_element = elements_connected_to_split[0]
        elements_connected_to_split.forEach((element) => {
          if (split_connection_direction == "b") {
            if (element.position.x > last_element.position.x) {
              last_element = element
            }
            if (element.position.x < first_element.position.x) {
              first_element = element
            }
            const connection_between_split_and_element = this.elements.find(
              (el) => el.source == split_element.id && el.target == element.id
            )
            connection_between_split_and_element.sourceHandle = split_connection_direction
          } else if (split_connection_direction == "l" || split_connection_direction == "r") {
            if (element.position.y > last_element.position.y) {
              last_element = element
            }
            if (element.position.y < first_element.y) {
              first_element = element
            }
            const connection_between_split_and_element = this.elements.find(
              (el) => el.source == split_element.id && el.target == element.id
            )
            connection_between_split_and_element.sourceHandle = split_connection_direction
          }
        })
        const connection_between_split_and_first_element = this.elements.find(
          (el) => el.source == split_element.id && el.target == first_element.id
        )
        const connection_between_split_and_last_element = this.elements.find(
          (el) => el.source == split_element.id && el.target == last_element.id
        )

        if (split_connection_direction == "b") {
          connection_between_split_and_first_element.sourceHandle = "l"
          connection_between_split_and_last_element.sourceHandle = "r"
        }

        if (split_connection_direction == "l" || split_connection_direction == "r") {
          connection_between_split_and_first_element.sourceHandle = "t"
          connection_between_split_and_last_element.sourceHandle = "b"
        }
      } else {
        this.handleDeleteNode(split_element.id)
      }
    },

    removeMergeOrRearrangeItsConnections(merge_element) {
      const elements_connected_to_merge = this.getConnectedSourceElements(merge_element.id, null, true)
      const link_between_merge_and_target_element = this.elements.find((el) => el.source == merge_element.id)
      const merge_connection_direction = merge_element.data.position

      if (elements_connected_to_merge.length == 1) {
        if (link_between_merge_and_target_element) {
          link_between_merge_and_target_element.source = elements_connected_to_merge[0].id
        }
        this.handleDeleteNode(merge_element.id)
      } else if (elements_connected_to_merge.length > 1) {
        let first_element = elements_connected_to_merge[0]
        let last_element = elements_connected_to_merge[0]
        elements_connected_to_merge.forEach((element) => {
          if (merge_connection_direction == "t") {
            if (element.position.x > last_element.position.x) {
              last_element = element
            }
            if (element.position.x < first_element.position.x) {
              first_element = element
            }
            const connection_between_merge_and_element = this.elements.find(
              (el) => el.target == merge_element.id && el.source == element.id
            )
            if (connection_between_merge_and_element) {
              connection_between_merge_and_element.targetHandle = merge_connection_direction
            }
          } else if (merge_connection_direction == "l" || merge_connection_direction == "r") {
            if (element.position.y > last_element.position.y) {
              last_element = element
            }
            if (element.position.y < first_element.position.y) {
              first_element = element
            }
            const connection_between_merge_and_element = this.elements.find(
              (el) => el.target == merge_element.id && el.source == element.id
            )
            connection_between_merge_and_element.sourceHandle = this.opposite_pos[merge_connection_direction]
          }
        })

        const connection_between_merge_and_first_element = this.elements.find(
          (el) => el.target == merge_element.id && el.source == first_element.id
        )
        const connection_between_merge_and_last_element = this.elements.find(
          (el) => el.target == merge_element.id && el.source == last_element.id
        )

        if (merge_connection_direction == "t") {
          if (connection_between_merge_and_first_element) {
            connection_between_merge_and_first_element.targetHandle = "l"
          }
          if (connection_between_merge_and_last_element) {
            connection_between_merge_and_last_element.targetHandle = "r"
          }
        }

        if (merge_connection_direction == "l" || merge_connection_direction == "r") {
          if (connection_between_merge_and_first_element) {
            connection_between_merge_and_first_element.targetHandle = "t"
          }
          if (connection_between_merge_and_last_element) {
            connection_between_merge_and_last_element.targetHandle = "b"
          }
        }
      } else {
        this.handleDeleteNode(merge_element.id)
      }
    },

    getConnectedElements(id, pos, only_first_level) {
      const links_from_element = this.elements.filter((el) => el.source == id && (pos ? el.sourceHandle == pos : true))
      let elements_linked = []
      links_from_element.forEach((link) => {
        const element_connected_to_link = this.elements.find((el) => el.id == link.target)

        if (element_connected_to_link?.data.type == "split" && !only_first_level) {
          const elements_connected_to_split = this.getConnectedElements(element_connected_to_link.id)
          elements_linked = elements_connected_to_split.concat(elements_linked)
        } else {
          elements_linked.push(element_connected_to_link)
        }
      })

      return elements_linked
    },

    /**
     * Retrieves the source elements connected to a given element by its ID.
     *
     * @param {string} id - The ID of the target element to find connected source elements for.
     * @param {string} [pos] - Optional position handle to filter connections by target handle.
     * @param {boolean} [flat=false] - If true, does not recursively find sources connected to "split" type elements.
     * @returns {Array} - An array of source elements connected to the given target element.
     * Example: getConnectedSourceElements('element_id', 't', false) -> [{ id: 'source_element_id', ... }]
     */
    getConnectedSourceElements(id, pos, flat) {
      const links_with_source_elements = this.elements.filter((el) => el.target == id && (pos ? el.targetHandle == pos : true))
      let sources_linked = []
      links_with_source_elements.forEach((link) => {
        const source_connected_to_link = this.elements.find((el) => el.id == link.source)
        if (source_connected_to_link.data.type == "split" && !flat) {
          const sources_connected_to_split = this.getConnectedSourceElements(source_connected_to_link.id)
          sources_linked = sources_connected_to_split.concat(sources_linked)
        } else {
          sources_linked.push(source_connected_to_link)
        }
      })
      return sources_linked
    },

    getNextConnectedTrigger(trigger_id) {
      const elements_connected_to_trigger = this.getConnectedElements(trigger_id)
      let find_operator_connected = elements_connected_to_trigger.find((el) => el.data.type == "operator")
      if (!find_operator_connected) {
        return null
      }
      const link_from_operator_to_next_trigger = this.elements.find((el) => el.source == find_operator_connected.id)
      if (!link_from_operator_to_next_trigger) {
        return null
      }
      const next_trigger = this.elements.find((el) => el.id == link_from_operator_to_next_trigger.target)
      return next_trigger
    },

    addMerge(trigger_ids, flow) {
      let trigger_ids_to_merge

      trigger_ids_to_merge = trigger_ids

      let id_calc = this.next_available_node_id

      const merge_obj = {
        id: `${id_calc}`,
        label: "",
        type: "circle",
        is_edge: false,
        flow: flow,
        data: { type: "merge", is_trigger_group_merge: true, position: "t" },
        events: {
          addNewItem: (e) => {
            this.handleAddNewItem(e)
          },
          click: (e) => {
            this.handleClickNode(e.node)
          },
          deleteClick: ($event) => {
            this.handleDeleteNode($event)
          },
          activateSourceConnection: (pos) => {
            if (this.source_connection_active) {
              this.source_connection_active = null
            } else {
              this.source_connection_active = {
                ...this.elements.find((el) => el.id == id_calc),
                pos: pos
              }
            }
          },
          activateTargetConnection: (pos) => {
            this.handleActivateTargetConnection(pos, id_calc)
          },

          hoverOverTargetConnection: (pos) => {
            this.handleHoverOverTargetConnection(pos, id_calc)
          },
          hoverAwayTargetConnection: () => {
            if (this.elements.find((el) => el.source == this.source_connection_active.id && el.target == id_calc)?.temp_line) {
              const index = this.elements.findIndex((el) => el.source == this.source_connection_active.id && el.target == id_calc)
              this.elements.splice(index, 1)
            }
          }
        }
      }

      const start_x_pos = this.elements.find((element) => element.id == trigger_ids_to_merge[0]).position.x
      const end_x_pos =
        this.elements.find((element) => element.id == trigger_ids_to_merge[trigger_ids_to_merge.length - 1]).position.x +
        this.node_dimensions.activity.trigger.w

      const middle_x_pos = start_x_pos + ((end_x_pos - start_x_pos) / 2 - this.node_dimensions.circle.merge.w / 2)
      merge_obj["position"] = {
        x: middle_x_pos,
        y: this.elements.find((element) => element.id == trigger_ids_to_merge[0]).position.y + this.node_dimensions.activity.trigger.h + 150
      }

      this.elements.push(merge_obj)

      trigger_ids_to_merge.forEach((trigger_id, i) => {
        const bottom_link_from_trigger = this.elements.find((el) => el.source == trigger_id && el.sourceHandle == "b")
        const element_connected_to_bottom_of_trigger = this.elements.find((el) => el.id == bottom_link_from_trigger?.target)

        if (element_connected_to_bottom_of_trigger?.data?.type == "split") {
          //split to merge
          const edge_obj = {
            id: `e${element_connected_to_bottom_of_trigger.id}b-${id_calc}t`,
            is_edge: true,
            source: `${element_connected_to_bottom_of_trigger.id}`,
            sourceHandle: "b",
            markerEnd: MarkerType.Arrow,

            target: `${id_calc}`,
            targetHandle: !i ? "l" : i == trigger_ids_to_merge.length - 1 ? "r" : "t",
            flow: flow,
            type: "custom",
            events: {
              deleteEdgeClick: ($event) => {
                this.handleDeleteEdge($event)
              }
            },
            style: {
              stroke: "black"
            }
          }

          this.elements.push(edge_obj)

          this.removeSplitOrRearrangeItsConnections(element_connected_to_bottom_of_trigger)
        } else if (element_connected_to_bottom_of_trigger) {
          const new_split = this.addNode(trigger_id, "circle", "split", "bottom", 5, "b", "t")

          //split to merge
          const edge_obj = {
            id: `e${new_split.id}b-${id_calc}t`,
            is_edge: true,
            source: `${new_split.id}`,
            sourceHandle: "b",
            markerEnd: MarkerType.Arrow,

            target: `${id_calc}`,
            targetHandle: !i ? "l" : i == trigger_ids_to_merge.length - 1 ? "r" : "t",
            flow: flow,
            type: "custom",
            events: {
              deleteEdgeClick: ($event) => {
                this.handleDeleteEdge($event)
              }
            },
            style: {
              stroke: "black"
            }
          }

          this.elements.push(edge_obj)

          //split to element on bottom
          const edge_obj_2 = {
            id: `e${new_split.id}b-${element_connected_to_bottom_of_trigger.id}t`,
            is_edge: true,
            source: `${new_split.id}`,
            sourceHandle: "b",
            markerEnd: MarkerType.Arrow,

            target: `${element_connected_to_bottom_of_trigger.id}`,
            targetHandle: "t",
            flow: flow,
            type: "custom",
            events: {
              deleteEdgeClick: ($event) => {
                this.handleDeleteEdge($event)
              }
            },
            style: {
              stroke: "black"
            }
          }

          this.elements.push(edge_obj_2)

          this.removeSplitOrRearrangeItsConnections(new_split)

          const link_between_trigger_and_element_connected_to_bottom_index = this.elements.findIndex(
            (el) => el.source == trigger_id && el.target == element_connected_to_bottom_of_trigger.id
          )
          this.elements.splice(link_between_trigger_and_element_connected_to_bottom_index, 1)
        } else {
          this.elements.push({
            id: `e${trigger_id}b-${id_calc}t`,
            is_edge: true,
            source: `${trigger_id}`,
            sourceHandle: "b",
            markerEnd: MarkerType.Arrow,

            target: `${id_calc}`,
            targetHandle: !i ? "l" : i == trigger_ids_to_merge.length - 1 ? "r" : "t",
            flow: flow,
            type: "custom",
            events: {
              deleteEdgeClick: ($event) => {
                this.handleDeleteEdge($event)
              }
            },
            style: {
              stroke: "black"
            }
          })
        }
      })

      return merge_obj
    }
  }
}
</script>
